import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { Place } from '../../../types/results.types';
import ResultsDetail from '../ResultsDetail';

interface ResultsDrawerProps {
  place: Place | null;
  isOpen: boolean;
  onClose: () => void;
  searchId?: number;
  shortLabels?: Record<string, string>;
  onUpgradeClick?: () => void;
  currentPlan?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
}

const ResultsDrawer: React.FC<ResultsDrawerProps> = ({
  place,
  isOpen,
  onClose,
  searchId,
  shortLabels = {},
  onUpgradeClick,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
}) => {
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = previousBodyOverflow;
    };
  }, [isOpen, handleEscapeKey]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <div 
        className={`fixed inset-0 z-40 bg-[#2D3142] transition-opacity duration-300 ${
          isOpen ? 'opacity-30' : 'pointer-events-none opacity-0'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div
        className={`fixed inset-y-0 right-0 z-50 w-full transform rounded-l-[28px] border-l border-[#EFE7E2] bg-[#FCFAF8] shadow-[0_24px_64px_rgba(45,49,66,0.18)] transition-all duration-300 ease-out sm:w-[95%] md:w-[min(46vw,760px)] md:min-w-[580px] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-end rounded-tl-[28px] border-b border-[#EEE6E1] bg-[#FCFAF8] px-5 py-4">
            <button
              onClick={onClose}
              className="rounded-full p-2 transition-all duration-200 ease-in-out hover:bg-white active:scale-95"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-[#7B7280]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
            <ResultsDetail
              place={place}
              searchId={searchId}
              isDrawerOpen={isOpen}
              shortLabels={shortLabels}
              onUpgradeClick={onUpgradeClick}
              currentPlan={currentPlan}
              autoFindMode={autoFindMode}
              autoFindSuccessLimit={autoFindSuccessLimit}
              autoFindSuccessCount={autoFindSuccessCount}
              autoFindAttemptCount={autoFindAttemptCount}
              autoFindNoEmailCount={autoFindNoEmailCount}
              autoFindCatchAllCount={autoFindCatchAllCount}
              autoFindGuardrailReason={autoFindGuardrailReason}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultsDrawer;
